import React, { useEffect, useState } from 'react';
import { client } from '../../../Common/gql/client';
import styles from './Filters.module.scss';
import classnames from 'classnames';
import SelectFilter from './Controls/SelectFilter';
import RadioFilter from './Controls/RadioFilter';
import { ContentType, Field } from '../../../Common/types/CatalogTypes';
import { HydratedCurriculumTypeDynamicFilter } from '../../../Common/types/AppConfigTypes';
import {
    filterOptionsQuery,
    getFilterOptionsQueryName,
} from '../../filterQuery';

export interface FilterProps {
    filter: HydratedCurriculumTypeDynamicFilter;
    currentContentType: ContentType;
    dynamicFilterIndex: number;
    currentValue: string;
    setFilterOpenState: (open: boolean, idx: number) => void;
    isOpen: boolean;
}

export interface FilterOption {
    id: string; // used primarily for the key
    value: string;
}

type ResultOption = { name?: string; type?: string; __typename: string };

const Filter: React.FC<FilterProps> = ({
    filter,
    currentContentType,
    dynamicFilterIndex,
    currentValue,
    isOpen,
    setFilterOpenState,
}) => {
    const toggleIcon = isOpen ? '-' : '+';
    const toggleStyles = isOpen ? styles.filterToggleOpen : styles.filterToggle;
    const labelStyles = isOpen ? styles.filterLabelOpen : styles.filterLabel;

    const filterField = filter.linkedTo.field;
    const filterContentType = filter.linkedTo.contentType;

    const label = (filterField && filterField.displayName) || '';
    const isLocalOptions = filterContentType.uuid === currentContentType.uuid;
    const [options, setOptions] = useState<FilterOption[]>([]);

    const dedupeOptions = (options: FilterOption[]) => {
        const unique: FilterOption[] = [];

        options.forEach(option => {
            let isUnique = true;

            // if it's found in unique, set is Unique to False
            unique.forEach(uniqueOpt => {
                if (option.value === uniqueOpt.value) {
                    isUnique = false;
                }
            });

            if (isUnique) {
                unique.push(option);
            }
        });

        return unique;
    };

    useEffect(() => {
        const getLocalOptionsFromField = (field: Field) => {
            if (!field.controls || !field.controls.settings.selectOptions) {
                return [];
            }
            const options = field.controls.settings.selectOptions.map(
                (option: string, index: number) => ({
                    id: `${field.uuid}-${index}`,
                    value: option,
                }),
            );

            return dedupeOptions(options);
        };

        const fetchLinkOptions = async (
            contentType: ContentType,
            field: Field,
        ) => {
            const result = await client.query({
                query: filterOptionsQuery(contentType, field),
            });

            const queryName = getFilterOptionsQueryName(contentType);
            const resultOptions = result.data[queryName].results;
            const formattedOptions = resultOptions.map(
                (item: ResultOption, index: number) => {
                    return {
                        id: `${field.uuid}-${index}`,
                        value: item[field.slug as keyof ResultOption],
                    };
                },
            );

            const dedupedOptions = dedupeOptions(formattedOptions);

            setOptions(dedupedOptions);
        };

        if (isLocalOptions) {
            setOptions(getLocalOptionsFromField(filterField));
        } else {
            fetchLinkOptions(filterContentType, filterField);
        }
    }, [isLocalOptions, filterField, filterContentType]);

    const handleFilterClick = () => {
        setFilterOpenState(!isOpen, dynamicFilterIndex);
    };

    const renderFilterControl = () => {
        if (!isOpen || !filter.control) {
            return null;
        }

        if (filter.control === 'dropdown') {
            return (
                <SelectFilter
                    options={options}
                    label={label}
                    dynamicFilterIndex={dynamicFilterIndex}
                    currentValue={currentValue}
                />
            );
        }

        if (filter.control === 'radio') {
            return (
                <RadioFilter
                    options={options}
                    label={label}
                    dynamicFilterIndex={dynamicFilterIndex}
                    currentValue={currentValue}
                />
            );
        }
    };

    return (
        <div
            id={'filters-filter-control-container'}
            className={styles.filterControlContainer}
        >
            <div className={styles.filterControl} onClick={handleFilterClick}>
                <div
                    id={classnames(
                        { 'filters-filter-toggle': !isOpen },
                        { 'filters-filter-toggle-open': isOpen },
                    )}
                    className={toggleStyles}
                >
                    {toggleIcon}
                </div>
                <div
                    id={classnames(
                        { 'filters-filter-label': !isOpen },
                        { 'filters-filter-label-open': isOpen },
                    )}
                    className={labelStyles}
                >
                    {filter.name}
                </div>
            </div>
            {renderFilterControl()}
        </div>
    );
};

export default Filter;
